import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check admin auth status on app load
  useEffect(() => {
    checkAdminAuthStatus();
  }, []);

  const checkAdminAuthStatus = () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) {
        setLoading(false);
        return;
      }

      // A token is present. The server validates it (and rejects expired ones)
      // on every admin API call, so we treat presence as logged-in here and let
      // the API layer force a re-login on 401.
      const adminEmail = localStorage.getItem('adminEmail') || '';
      setIsAdminLoggedIn(true);
      setAdminUser({ email: adminEmail });
    } catch (error) {
      console.error('Admin auth check failed:', error);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      setIsAdminLoggedIn(false);
      setAdminUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Verify credentials against the server, which returns a signed admin JWT.
  const adminLogin = async (email, password) => {
    try {
      const data = await adminAPI.login(email, password);
      const token = data.token;
      const adminEmail = data.admin?.email || email;

      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminEmail', adminEmail);
      setIsAdminLoggedIn(true);
      setAdminUser({ email: adminEmail });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Invalid credentials' };
    }
  };

  const adminLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    setIsAdminLoggedIn(false);
    setAdminUser(null);
  };

  const value = {
    isAdminLoggedIn,
    adminUser,
    loading,
    adminLogin,
    adminLogout,
    checkAdminAuthStatus
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
